import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    service: '',
    monthlySpend: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const services = [
    { value: 'ops', label: 'Marqi Ops - Operations as a Service' },
    { value: 'cast', label: 'MarqiCast - CTV for Agencies' },
    { value: 'rank', label: 'MarqiRank - AI Search Visibility' },
    { value: 'consultation', label: 'General Consultation' },
    { value: 'partnership', label: 'Partnership Opportunities' }
  ];

  const spendRanges = [
    { value: 'under-10k', label: 'Under $10K/month' },
    { value: '10k-50k', label: '$10K - $50K/month' },
    { value: '50k-100k', label: '$50K - $100K/month' },
    { value: '100k-250k', label: '$100K - $250K/month' },
    { value: 'over-250k', label: 'Over $250K/month' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Debug logging
      console.log('BACKEND_URL:', BACKEND_URL);
      console.log('Form data being sent:', {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        company: formData.company,
        service: formData.service,
        monthly_spend: formData.monthlySpend || null,
        message: formData.message
      });
      
      // Validate required fields before sending
      if (!formData.firstName || !formData.lastName || !formData.email || !formData.message) {
        toast.error("Please fill in all required fields (Name, Email, Message)");
        return;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/contacts/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          company: formData.company,
          service: formData.service,
          monthly_spend: formData.monthlySpend || null,
          message: formData.message
        })
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      const data = await response.json();
      console.log('Response data:', data);

      if (response.ok) {
        toast.success(data.message || "Thank you for your inquiry! We'll get back to you within 24 hours.", {
          duration: 5000,
          action: {
            label: "Close",
            onClick: () => console.log("Toast dismissed"),
          },
        });
        
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          company: '',
          service: '',
          monthlySpend: '',
          message: ''
        });
      } else {
        // Handle API validation errors properly
        if (data.detail && Array.isArray(data.detail)) {
          // Handle Pydantic validation errors
          const errorMessages = data.detail.map(err => err.msg || err.message || 'Validation error').join(', ');
          throw new Error(errorMessages);
        } else {
          throw new Error(data.detail || data.message || 'Form submission failed');
        }
      }
    } catch (error) {
      console.error('Contact form error:', error);
      
      // Ensure we always display a string, never an object
      let errorMessage = "Something went wrong. Please try again or contact us directly.";
      
      if (error.message && typeof error.message === 'string') {
        errorMessage = error.message;
      } else if (error.detail && typeof error.detail === 'string') {
        errorMessage = error.detail;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen py-20">
      <div className="container">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="display-md text-[var(--text-primary)] mb-6">
              Let's Accelerate Your Growth
            </h1>
            <p className="body-lg max-w-3xl mx-auto">
              Ready to scale your digital advertising? Get in touch with our team of experts 
              and discover how Marqi can transform your marketing performance.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div className="feature-card">
              <h2 className="h1 text-[var(--text-primary)] mb-8">Get Started Today</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-[var(--text-secondary)]">First Name *</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="bg-[var(--bg-secondary)] border-[var(--border-primary)] text-[var(--text-primary)] focus:border-[var(--accent-primary)]"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-[var(--text-secondary)]">Last Name *</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="bg-[var(--bg-secondary)] border-[var(--border-primary)] text-[var(--text-primary)] focus:border-[var(--accent-primary)]"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[var(--text-secondary)]">Work Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="bg-[var(--bg-secondary)] border-[var(--border-primary)] text-[var(--text-primary)] focus:border-[var(--accent-primary)]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company" className="text-[var(--text-secondary)]">Company *</Label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    className="bg-[var(--bg-secondary)] border-[var(--border-primary)] text-[var(--text-primary)] focus:border-[var(--accent-primary)]"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-[var(--text-secondary)]">Service Interest *</Label>
                  <Select value={formData.service} onValueChange={(value) => handleSelectChange('service', value)}>
                    <SelectTrigger className="bg-[var(--bg-secondary)] border-[var(--border-primary)] text-[var(--text-primary)]">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem key={service.value} value={service.value}>
                          {service.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-[var(--text-secondary)]">Current Monthly Ad Spend</Label>
                  <Select value={formData.monthlySpend} onValueChange={(value) => handleSelectChange('monthlySpend', value)}>
                    <SelectTrigger className="bg-[var(--bg-secondary)] border-[var(--border-primary)] text-[var(--text-primary)]">
                      <SelectValue placeholder="Select spend range" />
                    </SelectTrigger>
                    <SelectContent>
                      {spendRanges.map((range) => (
                        <SelectItem key={range.value} value={range.value}>
                          {range.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-[var(--text-secondary)]">Tell us about your goals *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="What challenges are you facing? What are your growth objectives?"
                    rows={4}
                    required
                    className="bg-[var(--bg-secondary)] border-[var(--border-primary)] text-[var(--text-primary)] focus:border-[var(--accent-primary)] resize-none"
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full btn-primary"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--bg-primary)] mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send className="ml-2 w-4 h-4" />
                    </>
                  )}
                </Button>

                <p className="text-sm text-[var(--text-muted)] text-center">
                  We typically respond within 24 hours during business days.
                </p>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div className="feature-card">
                <h3 className="h2 text-[var(--text-primary)] mb-6">Get in Touch</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[var(--accent-bg)] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-[var(--accent-primary)]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[var(--text-primary)] mb-1">Email Us</h4>
                      <p className="text-[var(--text-secondary)]">info@marqi.io</p>
                      <p className="text-sm text-[var(--text-muted)]">We'll respond within 24 hours</p>
                    </div>
                  </div>

                  {/* Phone number section removed as requested */}

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[var(--accent-bg)] rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[var(--accent-primary)]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[var(--text-primary)] mb-1">Visit Us</h4>
                      <p className="text-[var(--text-secondary)]">New York, NY</p>
                      <p className="text-sm text-[var(--text-muted)]">Meeting by appointment only</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Why Choose Marqi section removed as requested */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;